import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'angular-toastify';
import { distinctUntilChanged, startWith } from 'rxjs';
import { Question } from 'src/app/core/models/questionnaire.model';
import { QuestionnaireService } from 'src/app/core/services/survey.service';
import { cpfValidator } from '../../../utils/cpf.validator';
import { QuestionAnswer } from '../question/question.component';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { UserService } from 'src/app/core/services/user.service';
import { KycFieldLockService } from 'src/app/core/services/kyc-field-lock.service';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';
import { CaseService } from 'src/app/core/services/case.service';
import { ContractService } from 'src/app/core/services/contract.service';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { SignedContractService } from 'src/app/core/services/signed_contract.service';
import { ActivatedRoute, ParamMap, Route } from '@angular/router';

type ButtonState = 'check' | 'checking' | 'leaked' | 'not-leaked';

export interface AutofillFilter {
  op: string;
  col: string;
  val: string;
}

@Component({
  selector: 'app-question-cpf-leak-check',
  templateUrl: './question-cpf-leak-check.component.html',
  styleUrls: ['./question-cpf-leak-check.component.scss'],
})
export class QuestionCpfLeakCheckComponent implements OnInit {
  @Input('question') question!: Question;
  @Input() user: any;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  cpfControl = new FormControl('', [cpfValidator]);
  showErrors = false;
  buttonState: ButtonState = 'check';
  isLoading = false;
  skipFirstButtonCheck = false;
  autofillMode: boolean = false;
  autofillEditable: boolean = false;
  kycLocked: boolean = false;
  userId: string = '';

  constructor(
    private questionnaireService: QuestionnaireService,
    private toastService: ToastService,
    private translate: TranslateService,
    private dynamicService: DynamicService<any>,
    private userService: UserService,
    private claimService: ClaimsService,
    private activatedRoute: ActivatedRoute,
    private eventService: EventService,
    private kycFieldLock: KycFieldLockService
  ) {}

  ngOnInit(): void {
    this.initializeQuestion();
  }

  private async initializeQuestion() {
    if (!this.question.elective) {
      this.cpfControl.addValidators([Validators.required]);
    }

    await this.handleConfiguration();

    if (this.question.answer?.value) {
      this.cpfControl.setValue(this.question.answer.value.toString());

      if (this.question.answer.addtional_information) {
        const info = this.question.answer.addtional_information as {
          leaked: boolean;
          leaked_fields?: string[];
          offline?: boolean;
          checkedAt: Date;
        };

        if (info.leaked !== undefined) {
          this.buttonState = info.leaked ? 'leaked' : 'not-leaked';
          this.skipFirstButtonCheck = true;
        }
      }
    }

    if (this.kycLocked) {
      this.cpfControl.disable({ emitEvent: false });
    }

    this.setupValueChangeListener();

    // Notifica o estado inicial após configuração
    this.change();
  }

  private async handleConfiguration() {
    if (this.question.configuration) {
      const locked = await this.applyKycLock();
      if (
        !locked &&
        this.question.configuration.autofill &&
        !this.question.answer?.value
      ) {
        await this.processAutofill(this.question.configuration.autofill);
      }
    }
  }

  /**
   * CPK-5059: com fillInfo.lockWhenKycPassed e KYC aprovado, mostra o CPF do cadastro
   * (mesmo sobre um rascunho) e trava o campo. A checagem de vazamento continua disponível.
   */
  private async applyKycLock(): Promise<boolean> {
    if (!KycFieldLockService.lockedField(this.question)) return false;
    await this.fetchCurrentUserId();

    const lock = await this.kycFieldLock.resolve(this.question, this.userId);
    if (!lock.locked) return false;

    this.kycLocked = true;
    const sameCpf = this.question.answer?.value?.toString() === lock.value?.toString();
    this.question.answer = {
      value: lock.value,
      // O resultado da checagem só vale para o CPF que foi checado.
      ...(sameCpf && this.question.answer?.addtional_information
        ? { addtional_information: this.question.answer.addtional_information }
        : {}),
    };
    return true;
  }

  /** Controle desabilitado tem valid=false no Angular; o CPF travado vem do cadastro com KYC aprovado. */
  private get cpfAccepted(): boolean {
    return this.kycLocked || this.cpfControl.valid;
  }

  private async processAutofill(autofillConfig: any) {
    this.autofillMode = true;
    this.autofillEditable = autofillConfig.editable ?? false;
    await this.fetchCurrentUserId();

    const filters = this.replacePlaceholdersInFilters(autofillConfig.filters);
    const autofillValue = await this.fetchAutofillValue(
      autofillConfig.from,
      autofillConfig.select,
      filters
    );

    if (autofillValue) {
      this.question.answer = { value: autofillValue };
      this.cpfControl.setValue(autofillValue);
    }
  }

  private async fetchCurrentUserId() {
    if (this.user) {
      this.userId = this.user.id;
      return;
    }
    const currentUser = await this.userService.getCurrentUser();
    this.userId = currentUser?.id || '';
  }

  private replacePlaceholdersInFilters(
    filters: AutofillFilter[]
  ): AutofillFilter[] {
    return filters.map((filter) => ({
      ...filter,
      val: filter.val.replace('$userId', this.userId),
    }));
  }

  private async fetchAutofillValue(
    from: string,
    select: string,
    filters: AutofillFilter[]
  ): Promise<string | null> {
    try {
      this.dynamicService.load(from);
      const result = await this.dynamicService.autofillQuery(
        from,
        select,
        filters
      );

      const splitParts = (select as any).split('->>');
      const lastPart = splitParts[splitParts.length - 1];
      const cleanedLastPart = lastPart.replace(/['"]/g, '');

      return (
        result?.[0]?.[cleanedLastPart] ||
        result?.[0]?.[lastPart] ||
        result?.[0]?.[select as any] ||
        null
      );
    } catch (error) {
      console.error('Error fetching autofill value:', error);
      return null;
    }
  }

  private setupValueChangeListener() {
    this.cpfControl.valueChanges
      .pipe(
        distinctUntilChanged((prev: string | null, curr: string | null) => {
          return prev === curr;
        })
      )
      .subscribe((value) => {
        if (this.skipFirstButtonCheck) {
          this.skipFirstButtonCheck = false;
          return;
        }
        this.showErrors = false;

        // Se o campo foi apagado ou mudou, reseta o estado do botão
        if (!value || value.trim() === '' || this.buttonState !== 'check' && this.buttonState !== 'checking') {
          this.buttonState = 'check';
          if (this.question.answer) {
            this.question.answer.addtional_information = undefined;
          }
        }

        this.change();
      });
  }

  change() {
    const value = this.cpfControl.value;
    if (this.question.answer) {
      this.question.answer.value = value;
    }

    // Lógica de validação:
    // 1. Se não tem valor -> valida apenas o campo (cpfControl.valid)
    // 2. Se tem valor mas não checou -> inválido com mensagem
    // 3. Se checou (leaked ou not-leaked) -> válido
    if (!value || value.trim() === '') {
      // Campo vazio - valida apenas se o campo é válido (required/formato)
      this.question.isValid = this.cpfAccepted;
      this.question.messageError = '';
    } else if (this.buttonState === 'check') {
      // Tem valor mas não foi checado
      this.question.isValid = false;
      this.question.messageError = 'CPFLeakCheckMustBeChecked';
    } else if (this.buttonState === 'leaked' || this.buttonState === 'not-leaked') {
      // Foi checado (leaked ou not-leaked) - ambos são válidos
      this.question.isValid = true;
      this.question.messageError = '';
    } else {
      // Estado 'checking' - mantém o estado anterior
      // Não altera isValid nem messageError
    }

    this.changeAnswer.next({
      answer: { value: value || '' },
      question: this.question,
    });
  }

  onBlur() {
    this.showErrors = true;
  }

  async onCheckClick() {
    if (!this.cpfAccepted || this.isLoading) {
      return;
    }

    const cpfValue = this.cpfControl.value;
    if (!cpfValue) {
      return;
    }

    const cleanCpf = cpfValue.replace(/\D/g, '');

    this.buttonState = 'checking';
    this.isLoading = true;

    const eventTypeValue: EventTypes = EventTypes.CPF_CHECK;

    await this.sendEvent(eventTypeValue);

    try {
      // Try-catch específico para a chamada do serviço
      let response: any;
      let isOffline = false;
      try {
        response = await this.questionnaireService.checkCpfLeak(cleanCpf, {
          claimId: this.activatedRoute.snapshot.paramMap.get('claimId'),
          questionId: this.question?.id,
        });
      } catch (serviceError) {
        console.warn('CPF Leak Check - Erro no await do serviço, assumindo leaked=true por segurança', serviceError);
        response = { leaked: true, leaked_fields: [], offline: true };
        isOffline = true;
      }

      // Fallback de segurança: se a resposta não tiver o campo 'leaked' ou não for boolean, assume leaked = true
      let isLeaked = true;
      let leakedFields: string[] = [];

      if (response && typeof response.leaked === 'boolean') {
        isLeaked = response.leaked;
        leakedFields = Array.isArray(response.leaked_fields) ? response.leaked_fields : [];
        isOffline = response.offline === true;
      } else {
        console.warn('CPF Leak Check - Resposta inválida da API, assumindo leaked=true por segurança', response);
        isOffline = true;
      }

      this.buttonState = isLeaked ? 'leaked' : 'not-leaked';

      if (!this.question.answer) {
        this.question.answer = { value: cpfValue };
      }

      this.question.answer.addtional_information = {
        leaked: isLeaked,
        leaked_fields: leakedFields,
        offline: isOffline,
        checkedAt: new Date(),
      };

      // Após checagem, a questão é sempre válida (leaked ou not-leaked)
      this.question.isValid = true;
      this.question.messageError = '';

      const message = isLeaked
        ? this.translate.instant('CPFLeakCheckResultLeaked')
        : this.translate.instant('CPFLeakCheckResultNotLeaked');

      if (isLeaked) {
        this.toastService.success(message);
      } else {
        this.toastService.error(message);
      }

      this.changeAnswer.next({
        answer: this.question.answer,
        question: this.question,
      });
    } catch (error) {
      console.error('Error checking CPF leak:', error);

      // Fallback de segurança em caso de erro: assume leaked = true, offline = true
      console.warn('CPF Leak Check - Erro na chamada da API, assumindo leaked=true por segurança');

      this.buttonState = 'leaked';

      if (!this.question.answer) {
        this.question.answer = { value: cpfValue };
      }

      this.question.answer.addtional_information = {
        leaked: true,
        leaked_fields: [],
        offline: true,
        checkedAt: new Date(),
      };

      // Em caso de erro, assume leaked e marca como válido
      this.question.isValid = true;
      this.question.messageError = '';

      this.toastService.success(this.translate.instant('CPFLeakCheckResultLeaked'));

      this.changeAnswer.next({
        answer: this.question.answer,
        question: this.question,
      });
    } finally {
      this.isLoading = false;
    }
  }

  getButtonText(): string {
    switch (this.buttonState) {
      case 'check':
        return 'CPFLeakCheckButtonCheck';
      case 'checking':
        return 'CPFLeakCheckButtonChecking';
      case 'leaked':
      case 'not-leaked':
        return 'CPFLeakCheckButtonChecked';
      default:
        return 'CPFLeakCheckButtonCheck';
    }
  }

  isButtonDisabled(): boolean {
    return (
      !this.cpfAccepted ||
      this.isLoading ||
      this.buttonState === 'leaked' ||
      this.buttonState === 'not-leaked'
    );
  }

  private async sendEvent(eventType: EventTypes): Promise<any> {

    const caseId = await this.getCaseIdByClaimId();

    const eventTypeValue: EventTypes = eventType;

    const contextData: Object = {
      caseId: caseId,
    };

    try{
      await this.eventService.pushNew(eventTypeValue, contextData);
    }catch(error) {
      console.error('Event not sended, ', error);
    }
  }

  private async getCaseIdByClaimId(): Promise<string> {

    let caseId: string = '';

    const claimId = this.activatedRoute.snapshot.paramMap.get('claimId');

    if (!claimId) {
      console.warn('claimId not founded on URL');
      return caseId;
    }

    try{
       const getClaimsWithCase: any = await this.claimService.getClaimWithCase(claimId);

      if(!getClaimsWithCase?.case_id){
        console.warn('CaseID not founded.')
        return caseId;
      }

      caseId = getClaimsWithCase.case_id;
      return caseId;

    }catch(error){
      console.error('Error consulting caseID, ', error);
      return caseId;
    }

  }
}
